import { useState, useRef } from 'react'
import { useMount } from 'ahooks'
import { LeftOutline, RightOutline } from 'antd-mobile-icons'
import { Swiper, Toast, Image as AntdImage, ImageViewer } from 'antd-mobile'
import { useNavigate, useParams } from 'react-router-dom'
import { apis, utils, cal } from '@/utils'
import { t } from '@/utils/i18n'
import ProductNotFound from './components/ProductNotFound/ProductNotFound'
import DetailSkeleton from './components/DetailSkeleton/DetailSkeleton'
import ProductAttribute from './components/ProductAttribute/index'
import type { ProductDetail, ExchangeExtend } from './types'
import styles from './Detail.module.less'
import defaultImg from '@/assets/img/default/default.png'

const { thirdApi } = apis
const { to } = utils

function Detail() {
  const navigate = useNavigate()
  const { offerId } = useParams()

  const detailRef = useRef(null)
  const [contentLoader, setContentLoader] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [product, setProduct] = useState<ProductDetail>() // 货品实例
  const [mediaList, setMediaList] = useState<string[]>([]) // 媒体列表
  const [price, setPrice] = useState<string>('') // 价格
  const [descriptionImg, setDescriptionImg] = useState<string>('') // 描述图片
  const [showAttributePopup, setShowAttributePopup] = useState(false) // 属性弹窗

  /*
   * 返回
   */
  function handleGoBack() {
    navigate(-1)
  }

  /*
   * 图片轮播和预览
   */

  function handlePreviewImg(index: number) {
    if (mediaList[0] === defaultImg) {
      return
    }

    ImageViewer.Multi.show({
      images: mediaList,
      defaultIndex: index,
    })
  }

  // 轮播图
  const swiperItems = mediaList.map((item, index) => (
    <Swiper.Item key={index}>
      <AntdImage
        fit="cover"
        width={'100vw'}
        height={'100vw'}
        src={item}
        lazy={true}
        onClick={() => {
          handlePreviewImg(index)
        }}
      />
    </Swiper.Item>
  ))

  const createdSwiperIndicator = (total: number, current: number) => (
    <div className={styles['custom-indicator']}>{`${
      current + 1
    } / ${total}`}</div>
  )

  /*
   * 货品详情数据
   */
  // 初始化图片
  async function initMediaList(productDetail: ProductDetail) {
    const {
      productImage: { images },
    } = productDetail
    if (!images?.length) {
      setMediaList([defaultImg])
      return
    }

    setMediaList(images)
  }

  // 初始化价格
  async function initPrice(
    productDetail: ProductDetail,
    extend: ExchangeExtend,
  ) {
    if (productDetail.productSaleInfo.priceRangeList.length === 0) {
      return
    }

    const { exchangeRate, currency, decimalPlace } = extend
    setPrice(
      `${currency} ${cal.moneyFormat(
        Number(productDetail.productSaleInfo.priceRangeList[0].price) *
          exchangeRate,
        decimalPlace,
      )}`,
    )
  }

  // 过滤出宽度大于100的图片显示出来
  async function initDescriptionImg(productDetail: ProductDetail) {
    const { description } = productDetail
    if (!description) {
      return ''
    }

    const parser = new DOMParser()
    const htmlDoc = parser.parseFromString(description, 'text/html')
    const imgTags = htmlDoc.querySelectorAll('img')
    const imgTagsArray = Array.from(imgTags)
    const imgTagsStrArray = await Promise.all(
      imgTagsArray.map((img) => {
        return new Promise<string>((resolve) => {
          const image = new Image()
          image.onload = function () {
            if ((this as any).width < 80) {
              resolve('')
            } else {
              img.style.width = '100%'
              resolve(img.outerHTML)
            }
          }
          image.src = img.src
        })
      }),
    )
    setDescriptionImg(imgTagsStrArray.join(''))
  }

  // 获取货品数据
  async function getGoodsDetail() {
    const sendData = {
      offerId,
    }

    const [err, res] = await to(thirdApi.getAlibabaProductDetail(sendData))

    if (err) {
      setNotFound(true)
      return
    }

    const productDetail: ProductDetail = res.data
    const extend: ExchangeExtend = res.extend
    setProduct(productDetail)
    initMediaList(productDetail)
    initPrice(productDetail, extend)
    initDescriptionImg(productDetail)
    document.title = productDetail.subjectTrans || ''
  }

  // 数据初始化
  async function init() {
    Toast.show({
      icon: 'loading',
      content: t('加载中…'),
      duration: 0,
    })

    await getGoodsDetail()
    setContentLoader(false)

    Toast.clear()
  }

  useMount(() => {
    init()
  })

  /*
   * 渲染界面
   */
  if (contentLoader) {
    return <DetailSkeleton />
  }
  if (notFound) {
    return <ProductNotFound />
  }

  return (
    <div className={styles['detail-wrap']}>
      {/* 图片 */}
      <div className={styles['top-bar']}>
        <div className={styles['icon-wrap']} onClick={handleGoBack}>
          <LeftOutline color="#ffffff" fontSize={18} />
        </div>
      </div>
      <div className={styles['swiper-wrap']}>
        <Swiper
          style={{ '--width': '100vw', '--height': '100vw' }}
          indicator={createdSwiperIndicator}
        >
          {swiperItems}
        </Swiper>
      </div>

      <div className={styles['card-wrap'] + ' ' + styles['detail-info']}>
        {/* 货品价格 */}
        <div className={styles['price-wrap']}>
          <p className={styles['price']}>{price}</p>
        </div>

        {/* 货品标题 */}
        <h1 className={styles['product-title']}>
          <span>{product?.subjectTrans}</span>
          <span className={styles['no-fee-tag']}>{t('不含运费')}</span>
        </h1>
      </div>

      {product?.productAttribute?.length ? (
        <div className={styles['card-wrap']}>
          {/* 货品属性 */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
            onClick={() => {
              setShowAttributePopup(true)
            }}
          >
            <div style={{ fontSize: 18 }}>{t('货品属性')}</div>
            <RightOutline color="#ccc" fontSize={18} />
          </div>
          <ProductAttribute
            visible={showAttributePopup}
            attributes={product.productAttribute}
            onCancel={() => {
              setShowAttributePopup(false)
            }}
          />
        </div>
      ) : null}

      {/* 货品详情 */}
      {descriptionImg ? (
        <div
          ref={detailRef}
          className={styles['product-description']}
          dangerouslySetInnerHTML={{ __html: descriptionImg }}
        ></div>
      ) : null}
    </div>
  )
}

export default Detail
